/**
 * Event compliance gate.
 *
 * The organizer-authored text is the event's *compliance policy*. An attendee's
 * answer is a *compliance response*. This is deliberately distinct from the
 * global, once-per-user `isDataStorageConsentGiven` field on the user profile,
 * which is Congrs' own consent and says nothing about any particular event.
 */

export const COMPLIANCE_RESPONSE_STATUSES = {
  SEEN: "seen",
  ACCEPTED: "accepted",
  DECLINED: "declined",
  OPTED_OUT: "optedOut",
};

export const COMPLIANCE_RESPONSE_STATUS_VALUES = Object.values(
  COMPLIANCE_RESPONSE_STATUSES
);

/**
 * The three statuses an attendee can actually submit. `seen` is recorded by the
 * server when the gate is fetched, so it is deliberately not accepted here —
 * otherwise a client could downgrade an acceptance back to `seen`.
 *
 * `optedOut` is the withdrawal of an acceptance already on record, and is the
 * only status the server accepts *after* an acceptance. It is deliberately not
 * the same as `declined`: declining is never having agreed, opting out is
 * having agreed and then taken it back, and a compliance record that could not
 * tell those apart would be answering the wrong question.
 */
export const COMPLIANCE_ANSWER_STATUS_VALUES = [
  COMPLIANCE_RESPONSE_STATUSES.ACCEPTED,
  COMPLIANCE_RESPONSE_STATUSES.DECLINED,
  COMPLIANCE_RESPONSE_STATUSES.OPTED_OUT,
];

/**
 * The statuses that are an attendee's own deliberate answer, as opposed to the
 * `seen` row the server writes when the gate renders. Used wherever "did this
 * person actually answer" is the question: the timestamp shown against a row,
 * and the count of answers an organizer's edit would destroy.
 */
export const COMPLIANCE_ANSWERED_STATUS_VALUES = [
  COMPLIANCE_RESPONSE_STATUSES.ACCEPTED,
  COMPLIANCE_RESPONSE_STATUSES.DECLINED,
  COMPLIANCE_RESPONSE_STATUSES.OPTED_OUT,
];

export const COMPLIANCE_NEVER_OPENED = "neverOpened";

/**
 * The activity log's vocabulary.
 *
 * Every activity is something a *person* did with the terms: three answers, and
 * the departure that ends their coverage for good. That is the whole list on
 * purpose — the log is read as evidence about attendees, and a row that is not
 * about one is noise in the middle of it.
 *
 * The three answers mirror the response statuses, because an answer is both a
 * change of current state and a thing that happened. `accountDeleted` has no
 * status to mirror: it is here rather than a per-event tally because "a ticket
 * holder left" is a dated activity with a name on it, and a counter can only
 * ever say how many.
 *
 * The gate also records a `seen` *response* when it renders, which is what
 * makes the "saw the terms and did not answer" insights bucket computable. That
 * is current state, not an activity: nobody did anything.
 */
export const COMPLIANCE_ACTIVITIES = {
  ACCEPTED: "accepted",
  DECLINED: "declined",
  OPTED_OUT: "optedOut",
  ACCOUNT_DELETED: "accountDeleted",
};

export const COMPLIANCE_ACTIVITY_VALUES = Object.values(COMPLIANCE_ACTIVITIES);

const eventIdParams = {
  type: "object",
  properties: {
    eventId: { type: "string" },
  },
  required: ["eventId"],
};

export const getCompliancePolicySchema = {
  params: eventIdParams,
};

export const putCompliancePolicySchema = {
  params: eventIdParams,
  body: {
    type: "object",
    properties: {
      title: { type: "string", maxLength: 255 },
      body: { type: "string" },
    },
    required: ["title", "body"],
    additionalProperties: false,
  },
};

export const getComplianceActivitySchema = {
  params: eventIdParams,
  querystring: {
    type: "object",
    properties: {
      activity: { type: "string", enum: COMPLIANCE_ACTIVITY_VALUES },
      search: { type: "string", maxLength: 255 },
      page: { type: "number" },
      limit: { type: "number" },
    },
    additionalProperties: false,
  },
};

export const exportComplianceActivitySchema = {
  params: eventIdParams,
  querystring: {
    type: "object",
    properties: {
      activity: { type: "string", enum: COMPLIANCE_ACTIVITY_VALUES },
      search: { type: "string", maxLength: 255 },
    },
    additionalProperties: false,
  },
};

export const getComplianceGateSchema = {
  params: eventIdParams,
};

/**
 * The five mutually exclusive buckets an event's ticket holders fall into,
 * against the event's active ticket count. `neverOpened` is the absence of a
 * response row, not the absence of a login — see the compliance insights
 * section for why those are not the same thing.
 */
export const getComplianceInsightsSchema = {
  params: eventIdParams,
};

export const postComplianceResponseSchema = {
  params: eventIdParams,
  body: {
    type: "object",
    properties: {
      status: { type: "string", enum: COMPLIANCE_ANSWER_STATUS_VALUES },
      platform: { type: "string", maxLength: 50 },
      appVersion: { type: "string", maxLength: 50 },
    },
    required: ["status"],
    additionalProperties: false,
  },
};
